import type { JSX } from 'react';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { Text } from 'Components/Text/Text';
import { GenericTooltip, IconColor } from 'Components/Tooltips/GenericTooltip';
import { ICON_HELP, ICON_NEGATIVE, ICON_NEUTRAL, ICON_POSITIVE, ICON_PROHIBITED } from 'Config/Icons';
import { makeComplianceRequirementHumanReadable } from 'Helpers/ComplianceRequirementFormatter/ComplianceRequirementFormatter';
import { StatusCategory } from 'Models/ComplianceRequirements';

import styles from './ComplianceRequirementStatus.module.css';

export interface ComplianceRequirementStatusIconProps {
    regulation: string;
    status?: string | null;
}

/** A component for showing an icon representing a Compliance Requirement's status. Since possible statuses vary based on Regulation, icons are based on a status category. */
export const ComplianceRequirementStatusIcon = (props: ComplianceRequirementStatusIconProps): JSX.Element => {
    const { getComplianceStatusDefinition } = useCachedData();

    const definition = getComplianceStatusDefinition(props.regulation, props.status);
    const category = definition?.category;

    switch (category) {
        case StatusCategory.MATURITY:
            return (
                <Text variant="Text3" noStyles>
                    {definition?.value}
                </Text>
            );
        case StatusCategory.NEGATIVE:
            return <GenericTooltip text={props.status ?? StatusCategory.NEGATIVE} fontAwesomeIcon={ICON_NEGATIVE} iconColor={IconColor.RED} />;
        case StatusCategory.NOT_APPLICABLE:
            return <GenericTooltip text={props.status ?? StatusCategory.NOT_APPLICABLE} fontAwesomeIcon={ICON_PROHIBITED} iconColor={IconColor.GRAY} />;
        case StatusCategory.PARTIAL:
            return <GenericTooltip text={props.status ?? StatusCategory.PARTIAL} fontAwesomeIcon={ICON_NEUTRAL} iconColor={IconColor.YELLOW} />;
        case StatusCategory.POSITIVE:
            return <GenericTooltip text={props.status ?? StatusCategory.POSITIVE} fontAwesomeIcon={ICON_POSITIVE} iconColor={IconColor.GREEN} />;
        case StatusCategory.UNKNOWN:
            return <GenericTooltip text={props.status ?? StatusCategory.UNKNOWN} fontAwesomeIcon={ICON_HELP} iconColor={IconColor.GRAY} />;
        default:
            return (
                <Text variant="Text3" noStyles>
                    -
                </Text>
            );
    }
};

export interface ComplianceRequirementStatusIconAndStatusTextProps {
    regulation: string;
    status?: string | null;
}

/**
 * A component for showing an icon representing a Compliance Requirement's status and the status text.
 * If `status` is undefined or null, "-" is shown without an icon.
 * Since possible statuses vary based on Regulation, icons are based on a status category.
 */
export const ComplianceRequirementStatusIconAndStatusText = (props: ComplianceRequirementStatusIconAndStatusTextProps): JSX.Element => {
    const { getComplianceStatusDefinition } = useCachedData();

    return (
        <div className={styles.statusIconAndText}>
            {props.status && <ComplianceRequirementStatusIcon regulation={props.regulation} status={props.status} />}
            <Text variant="Text3" color="darkGray" noStyles>
                {props.status ? getComplianceStatusDefinition(props.regulation, props.status)?.value : '-'}
            </Text>
        </div>
    );
};

export interface ComplianceRequirementControlSetStatusTooltipProps {
    regulation: string;
    requirementId?: string;
    status?: string;
}

/** A component for explaining why a Compliance Requirement's ControlSet has a particular status, based on the statuses of the Controls that comprise it. */
export const ComplianceRequirementControlSetStatusTooltip = (props: ComplianceRequirementControlSetStatusTooltipProps): JSX.Element => {
    const { getComplianceStatusDefinition } = useCachedData();
    const definition = getComplianceStatusDefinition(props.regulation, props.status);
    const category = definition?.category;
    const requirementName = props.requirementId ? makeComplianceRequirementHumanReadable(props.regulation, props.requirementId) : 'this requirement';

    switch (category) {
        case StatusCategory.NEGATIVE:
            return <GenericTooltip text={`This control set has a "${StatusCategory.NEGATIVE}" status because each control assessment assigned a "${StatusCategory.NEGATIVE}" status to ${requirementName}.`}></GenericTooltip>;
        case StatusCategory.NOT_APPLICABLE:
            return <GenericTooltip text={`This control set has a "${StatusCategory.NOT_APPLICABLE}" status because each control assessment assigned a "${StatusCategory.NOT_APPLICABLE}" status to ${requirementName}.`}></GenericTooltip>;
        case StatusCategory.PARTIAL:
            return <GenericTooltip text={`This control set has a "${StatusCategory.PARTIAL}" status because at least one control assessment assigned a "${StatusCategory.PARTIAL}" status to ${requirementName}.`}></GenericTooltip>;
        case StatusCategory.UNKNOWN:
            return <GenericTooltip text={`This control set has an "${StatusCategory.UNKNOWN}" status because at least one control assessment assigned an "${StatusCategory.UNKNOWN}" status to ${requirementName}.`}></GenericTooltip>;
        case StatusCategory.POSITIVE:
            return <GenericTooltip text={`This control set has a "${StatusCategory.POSITIVE}" status because at least one control assessment assigned a "${StatusCategory.POSITIVE}" status to ${requirementName}.`}></GenericTooltip>;
        default:
            return <GenericTooltip text={`Additional control assessment(s) needed to assign a status to this control set.`}></GenericTooltip>;
    }
};

export interface ComplianceRequirementOverallStatusTooltipProps {
    assignedControlSetCount: number;
    overallStatus?: string;
    regulation: string;
    requirementId: string;
}

/** A component for explaining why a Compliance Requirement has a particular status, based on the statuses of the ControlSets that comprise it. */
export const ComplianceRequirementOverallStatusTooltip = (props: ComplianceRequirementOverallStatusTooltipProps): JSX.Element => {
    const { getComplianceStatusDefinition } = useCachedData();
    const definition = getComplianceStatusDefinition(props.regulation, props.overallStatus);
    const category = definition?.category;
    const requirementName = makeComplianceRequirementHumanReadable(props.regulation, props.requirementId);

    switch (category) {
        case StatusCategory.NEGATIVE:
            return <GenericTooltip text={`${requirementName} has a "${StatusCategory.NEGATIVE}" status because at least one control set assigned a "${StatusCategory.NEGATIVE}" status.`}></GenericTooltip>;
        case StatusCategory.NOT_APPLICABLE:
            return <GenericTooltip text={`${requirementName} has a "${StatusCategory.NOT_APPLICABLE}" status because each control set assigned a "${StatusCategory.NOT_APPLICABLE}" status.`}></GenericTooltip>;
        case StatusCategory.PARTIAL:
            return <GenericTooltip text={`${requirementName} has a "${StatusCategory.PARTIAL}" status because at least one control set assigned a "${StatusCategory.PARTIAL}" status.`}></GenericTooltip>;
        case StatusCategory.UNKNOWN:
            return <GenericTooltip text={`${requirementName} has an "${StatusCategory.UNKNOWN}" status because at least one control set assigned an "${StatusCategory.UNKNOWN}" status.`}></GenericTooltip>;
        case StatusCategory.POSITIVE:
            return <GenericTooltip text={`${requirementName} has a "${StatusCategory.POSITIVE}" status because each control set assigned a "${StatusCategory.POSITIVE}" status.`}></GenericTooltip>;
        default:
            return <GenericTooltip text={`Additional control assessment(s) needed to assign a status to this requirement.`}></GenericTooltip>;
    }
};
